/**
 * Application Router Configuration
 */

import { createBrowserRouter, Navigate } from "react-router-dom";
import { HomePage } from "./pages/HomePage";
import { BuilderPage } from "./pages/BuilderPage";
import { ConfigurePage } from "./pages/ConfigurePage";

export const router = createBrowserRouter([
  {
    // Default: redirect to builder with new session
    path: "/",
    element: <Navigate to={`/builder/${generateSessionId()}`} replace />,
  },
  {
    path: "/home",
    element: <HomePage />,
  },
  {
    path: "/builder",
    element: <Navigate to={`/builder/${generateSessionId()}`} replace />,
  },
  {
    path: "/builder/:sessionId",
    element: <BuilderPage />,
  },
  {
    // Legacy support: redirect /session/:id to /builder/:id
    path: "/session/:sessionId",
    element: <LegacySessionRedirect />,
  },
  {
    path: "/configure",
    element: <ConfigurePage />,
  },
  {
    // Public gallery of user-published assemblies
    path: "/catalog",
    lazy: async () => ({ Component: (await import("./pages/CatalogPage")).CatalogPage }),
  },
  {
    // Admin console: access history and exported robots. Loaded on
    // demand so its tables and labels stay out of the bundle every
    // visitor downloads; non-admins are bounced by the page itself and
    // by every endpoint behind it, which answer 404.
    path: "/admin",
    lazy: async () => ({ Component: (await import("./pages/AdminPage")).AdminPage }),
  },
]);

/**
 * Generate a cryptographically secure random session ID.
 * Uses crypto.getRandomValues() (128 bits of entropy).
 */
function generateSessionId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Redirect legacy /session/:id URLs to /builder/:id
 */
function LegacySessionRedirect() {
  const sessionId = window.location.pathname.match(/^\/session\/([a-zA-Z0-9_-]+)/)?.[1];
  if (sessionId) {
    return <Navigate to={`/builder/${sessionId}`} replace />;
  }
  return <Navigate to="/" replace />;
}
